import axios from "axios";
import axiosInstance from "utils/request";
import { CDN_DOMAIN } from "config";
import {
  DatasetSchema,
  DatasetDetailSchema,
  DatasetImageSchema,
  DefectDatasetSchema,
  DefectDatasetDetailSchema,
  type Dataset,
  type DatasetDetail,
  type DatasetImage,
  type DefectDataset,
  type DefectDatasetDetail,
  ImageAnnotationSchema,
  type ImageAnnotation,
} from "./schemas";

export type {
  Dataset,
  DatasetDetail,
  DatasetImage,
  DefectDataset,
  DefectDatasetDetail,
  ImageAnnotation
};

export const PAGE_SIZE = 100;

export type DatasetKind = "object" | "defect";

export function cdnImageUrl(image: DatasetImage): string {
  return `${CDN_DOMAIN}/${image.region}/image/${image.project_id}${image.src}`;
}

export function cdnDatasetImageUrl(
  region: string,
  datasetId: number,
  path: string
): string {
  return `${CDN_DOMAIN}/${region}/dataset_image/${datasetId}/${path}`;
}

export async function listDatasetImageCrops(
  region: string,
  datasetId: number,
  imageId: number,
  kind: DatasetKind,
  signal?: AbortSignal
): Promise<string[]> {
  const response = await axios.get<string[]>(
    `${CDN_DOMAIN}/${region}/dataset_image/list/${datasetId}/${imageId}`,
    {
      params: { defect: kind === "defect" },
      signal,
      withCredentials: true,
    }
  );
  return response.data;
}

async function getDatasets(): Promise<Dataset[]> {
  const response = await axiosInstance.get("/datasets");
  return DatasetSchema.array().parse(response.data);
}

async function getDataset(datasetId: number): Promise<DatasetDetail> {
  const response = await axiosInstance.get(`/datasets/${datasetId}`);
  return DatasetDetailSchema.parse(response.data);
}

async function getDatasetImages(
  datasetId: number,
  lastId: number,
  signal?: AbortSignal
): Promise<DatasetImage[]> {
  const response = await axiosInstance.get(`/datasets/${datasetId}/images`, {
    params: { last_id: lastId, limit: PAGE_SIZE },
    signal,
  });
  return DatasetImageSchema.array().parse(response.data);
}

export async function getImageAnnotations(
  region: string,
  projectId: number,
  imageId: number,
  signal?: AbortSignal
): Promise<ImageAnnotation[]> {
  const response = await axiosInstance.get(
    `/${region}/${projectId}/${imageId}/annotations`,
    { signal }
  );
  return ImageAnnotationSchema.array().parse(response.data);
}
async function getDefectDatasets(): Promise<DefectDataset[]> {
  const response = await axiosInstance.get("/defect-datasets");
  return DefectDatasetSchema.array().parse(response.data);
}

async function getDefectDataset(
  datasetId: number
): Promise<DefectDatasetDetail> {
  const response = await axiosInstance.get(`/defect-datasets/${datasetId}`);
  return DefectDatasetDetailSchema.parse(response.data);
}

async function getDefectDatasetImages(
  datasetId: number,
  lastId: number,
  signal?: AbortSignal
): Promise<DatasetImage[]> {
  const response = await axiosInstance.get(
    `/defect-datasets/${datasetId}/images`,
    {
      params: { last_id: lastId, limit: PAGE_SIZE },
      signal,
    }
  );
  return DatasetImageSchema.array().parse(response.data);
}

interface DatasetApiEntry {
  list: () => Promise<Dataset[] | DefectDataset[]>;
  detail: (id: number) => Promise<DatasetDetail | DefectDatasetDetail>;
  images: (
    id: number,
    lastId: number,
    signal?: AbortSignal
  ) => Promise<DatasetImage[]>;
  basePath: `/${string}`;
}

export const DATASET_API: Record<DatasetKind, DatasetApiEntry> = {
  object: {
    list: getDatasets,
    detail: getDataset,
    images: getDatasetImages,
    basePath: "/datasets",
  },
  defect: {
    list: getDefectDatasets,
    detail: getDefectDataset,
    images: getDefectDatasetImages,
    basePath: "/datasets/defect",
  },
};
